import { convexQuery } from '@convex-dev/react-query'
import { Trans } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { BrandLoadingScreen } from '~/components/brand/brand-loader'
import { BriefingFillForm } from '~/components/funnel/briefing-fill-form'
import { BrandStrip, PublicMessage, PublicShell } from '~/components/funnel/public-shell'
import { capture, setWorkspaceGroup } from '~/lib/posthog'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/b/$token')({
  component: RouteComponent,
  // Token-bearing client links must never become search results or leak their
  // path through outbound Referer headers. robots.txt is only advisory, so
  // keep the document-level policies even for crawlers that ignore it.
  head: () => ({
    meta: [
      { content: 'noindex, nofollow', name: 'robots' },
      { content: 'no-referrer', name: 'referrer' },
    ],
  }),
  // Public unauthenticated surface — runs entirely against the Convex client.
  ssr: false,
})

function RouteComponent() {
  const { token } = Route.useParams()
  const { data: briefing, isPending } = useQuery(
    convexQuery(api.formResponses.getByToken, { token })
  )
  const [submitted, setSubmitted] = useState(false)
  const tracked = useRef(false)

  // Anonymous — the client filling this in is a third party, never
  // identified, and nothing they type is sent as an event property. They are
  // still grouped: the workspace is what this event is FOR, and the group is
  // set first so the capture on the next line carries it. A dead link has no
  // workspace to name and reports ungrouped, which is the honest shape.
  useEffect(() => {
    if (briefing === undefined || tracked.current) {
      return
    }
    tracked.current = true
    if ('workspaceId' in briefing && briefing.workspaceId) {
      setWorkspaceGroup(briefing.workspaceId)
    }
    capture('client_link_opened', { kind: 'briefing', status: briefing.status })
  }, [briefing])

  if (isPending || briefing === undefined) {
    return <BrandLoadingScreen />
  }

  if (briefing.status === 'not_found') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This briefing link is not valid.</Trans>}
          title={<Trans>Link not found</Trans>}
        />
      </PublicShell>
    )
  }

  if (briefing.status === 'revoked' || briefing.status === 'expired') {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>This link is no longer valid.</Trans>}
          title={<Trans>Link expired</Trans>}
        />
      </PublicShell>
    )
  }

  if (briefing.status === 'submitted' || submitted) {
    return (
      <PublicShell>
        <PublicMessage
          body={<Trans>We will be in touch soon. Thank you!</Trans>}
          title={<Trans>Briefing received</Trans>}
        />
      </PublicShell>
    )
  }

  return (
    <PublicShell>
      <div className="overflow-hidden rounded-xl bg-background shadow-card">
        <BrandStrip color={briefing.brandColor} />
        <div className="p-6">
          <BriefingFillForm
            onSubmitted={() => setSubmitted(true)}
            sections={briefing.template.sections}
            token={token}
            workspaceLogo={briefing.workspaceLogo}
            workspaceName={briefing.workspaceName}
          />
        </div>
      </div>
    </PublicShell>
  )
}
